import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  PayloadAction,
} from "@reduxjs/toolkit"

import { Client, DataStatus, RootState, Sorter, Team, TeamStats } from "@/types"
import { fetchTeamsStats } from "@/utils"
import { TeamWithStats } from "@/views/EventsView/EventsStatistics/TeamStats"

import { selectCurrentMultiTeams } from "./teams"
import { clearDataOnLogout } from "./user"

const teamsStatsAdapter = createEntityAdapter<TeamStats>()

type TeamsStatsState = EntityState<TeamStats> & {
  status: DataStatus | "updating" | null
}

const initialState: TeamsStatsState = teamsStatsAdapter.getInitialState({
  status: "no_team",
})

export const getTeamStats = createAsyncThunk("teamsStats/getTeamStats", async (teamsIds: Array<Team["id"]>) => {
  const { response, json } = await fetchTeamsStats(teamsIds)

  return {
    ok: response.ok,
    status: response.status,
    data: json.data,
  }
})

const teamsStatsSlice = createSlice({
  name: "teamsStats",
  initialState,
  reducers: {
    updateTeamStats(state, action: PayloadAction<TeamStats>) {
      teamsStatsAdapter.upsertOne(state, action.payload)
    },
    updateTeamsStats(state, action: PayloadAction<TeamStats[]>) {
      teamsStatsAdapter.upsertMany(state, action.payload)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTeamStats.pending, (state) => {
      if (state.ids.length) {
        state.status = "updating"
      } else {
        state.status = "loading"
      }
    })

    builder.addCase(getTeamStats.fulfilled, (state, { payload }) => {
      if (payload.ok) {
        if (payload.data) {
          teamsStatsAdapter.upsertMany(state, payload.data)
        }
        state.status = "fetched"
      } else {
        state.status = "error"
      }
    })

    builder.addCase(clearDataOnLogout, () => initialState)
  },
})

const { selectById, selectIds, selectAll } = teamsStatsAdapter.getSelectors((state: RootState) => state.teamsStats)

export const selectTeamsStatsIds = (state: RootState) => selectIds(state)

export const selectTeamsStats = (state: RootState) => selectAll(state)

export const selectTeamStatsById = (id: Client["id"]) => (state: RootState) => selectById(state, id)

export const selectCurrentTeamStats = (state: RootState) =>
  state.teams.current && state.teamsStats.entities[state.teams.current.id]

export const selectCurrentTeamStatsKey = <T extends keyof TeamStats>(key: T) =>
  createSelector(selectCurrentTeamStats, (teamStats) => teamStats?.[key])

export const selectSelectedTeamsWithStats = createSelector(
  selectCurrentMultiTeams,
  selectTeamsStats,
  (selectedTeams, teamsStats) =>
    selectedTeams.map((team) => ({ team, stats: teamsStats.find((stats) => stats.id === team.id) }))
)

export const selectSortedSelectedTeamsWithStats = (sorter: Sorter<TeamWithStats>) =>
  createSelector(selectSelectedTeamsWithStats, (teamsWithStats) => teamsWithStats.sort(sorter))

export const selectAreAllSelectedTeamsStatsLoaded = createSelector(selectSelectedTeamsWithStats, (teamsWithStats) =>
  Object.values(teamsWithStats).reduce((areLoaded, team) => (team.stats ? areLoaded : false), true)
)

export const selectMultiTeamsTotalDeliveryOrdersCount = (state: RootState) =>
  state.teams.currentMulti
    .map((multiTeam) => state.teamsStats.entities[multiTeam.id]?.today_delivery_orders_count)
    .reduce((prev, curr) => Number(prev) + Number(curr), 0)

export const selectTeamsStatsStatus = (state: RootState) => state.teamsStats.status

export const { updateTeamStats, updateTeamsStats } = teamsStatsSlice.actions
export const teamsStatsReducer = teamsStatsSlice.reducer
